import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';

export interface MatchingCompany {
  company_id: string | number;
  ticker_id: string | number;
  ticker_symbol: string;
  ticker_name: string;
  exchange?: string;
  logo_url?: string;
  market_cap?: number | string;
  market_cap_formatted?: string;
}

interface MatchingCompanyRowProps {
  company: MatchingCompany;
}

const MatchingCompanyRow: React.FC<MatchingCompanyRowProps> = ({ company }) => {
  const {
    company_id,
    ticker_id,
    ticker_symbol,
    ticker_name,
    exchange,
    logo_url,
    market_cap_formatted,
    market_cap,
  } = company;

  const href = `/ticker/${ticker_id}/${exchange ?? ''}/${ticker_symbol}?type=company&cid=${company_id}`;
  const marketCapDisplay = market_cap_formatted ?? (market_cap != null ? String(market_cap) : '—');
  const initials = (ticker_symbol || ticker_name || '').slice(0, 2).toUpperCase();

  return (
    <li>
      <a
        href={href}
        className="group flex w-full items-center gap-3 px-2 py-3 transition-colors hover:bg-slate-50/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-emerald-600 sm:gap-4 sm:py-3.5 md:px-4 lg:px-6"
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-slate-100 bg-gray-700 sm:h-9 sm:w-9">
          {logo_url ? (
            <img
              src={logo_url}
              alt={ticker_name || ticker_symbol}
              loading="lazy"
              className="h-4 w-4 object-contain sm:h-5 sm:w-5"
            />
          ) : (
            <span className="text-[9px] font-bold text-gray-400 sm:text-[10px]">{initials}</span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-bold tracking-tight text-slate-900">
              {ticker_symbol}
            </span>
            {exchange ? (
              <span className="hidden shrink-0 text-[10px] font-medium uppercase tracking-wider text-slate-400 sm:inline">
                {exchange}
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 truncate text-xs font-medium text-slate-500">
            {ticker_name}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          <div className="flex flex-col items-end">
            <span className="text-[10px] font-medium uppercase tracking-wider text-slate-400">
              Mkt cap
            </span>
            <span className="text-sm font-semibold tabular-nums text-slate-900">
              {marketCapDisplay}
            </span>
          </div>
          <FontAwesomeIcon
            icon={faArrowUpRightFromSquare}
            className="hidden text-[10px] text-emerald-600 transition-colors group-hover:text-emerald-700 sm:block sm:text-xs"
            aria-hidden
          />
        </div>
      </a>
    </li>
  );
};

export default MatchingCompanyRow;
